import { supabase, BudgetDB, BudgetItemDB } from '../lib/supabase';
import { Budget, BudgetItem, ServiceType } from '../types';

// Converte do formato do banco para o formato da aplicação
const dbToApp = (budget: BudgetDB, items: BudgetItemDB[]): Budget => {
    // Parsear cômodos
    let rooms: Budget['rooms'] = [];
    if (budget.rooms) {
        if (typeof budget.rooms === 'string') {
            try {
                rooms = JSON.parse(budget.rooms);
            } catch (e) { rooms = []; }
        } else {
            rooms = budget.rooms;
        }
    }

    return {
        id: budget.id,
        client_name: budget.client_name,
        client_phone: budget.client_phone || undefined,
        notes: budget.notes || undefined,
        width: budget.width || 0,
        depth: budget.depth || 0,
        height: budget.height || 0,
        rooms: rooms || [],
        items: items.map(item => ({
            id: item.id,
            room_id: item.room_id || undefined,
            service_type: item.service_type,
            quantity: Number(item.quantity),
            unit_price: Number(item.unit_price),
            subtotal: Number(item.subtotal),
        })),
        selected_services: Array.from(new Set(items.map(item => item.service_type))),
        total_value: Number(budget.total_value),
        status: budget.status,
        created_at: budget.created_at,
    };
};

export const budgetService = {
    // Listar orçamentos do usuário
    async getBudgets(userId: string): Promise<Budget[]> {
        const { data: budgets, error: budgetsError } = await supabase
            .from('budgets')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (budgetsError) {
            console.error('Erro ao buscar orçamentos:', budgetsError);
            return [];
        }

        if (!budgets || budgets.length === 0) return [];

        // Buscar itens de todos os orçamentos
        const budgetIds = budgets.map(b => b.id);
        const { data: items, error: itemsError } = await supabase
            .from('budget_items')
            .select('*')
            .in('budget_id', budgetIds);

        if (itemsError) {
            console.error('Erro ao buscar itens:', itemsError);
        }

        const itemsByBudget = (items || []).reduce((acc, item) => {
            if (!acc[item.budget_id]) acc[item.budget_id] = [];
            acc[item.budget_id].push(item as BudgetItemDB);
            return acc;
        }, {} as Record<string, BudgetItemDB[]>);

        return budgets.map(budget =>
            dbToApp(budget as BudgetDB, itemsByBudget[budget.id] || [])
        );
    },

    // Criar novo orçamento
    async createBudget(
        userId: string,
        budgetData: Omit<Budget, 'id' | 'created_at'>
    ): Promise<Budget | null> {
        // Inserir orçamento
        const { data: budget, error: budgetError } = await supabase
            .from('budgets')
            .insert({
                user_id: userId,
                client_name: budgetData.client_name,
                client_phone: budgetData.client_phone || null,
                notes: budgetData.notes || null,
                total_value: budgetData.total_value,
                status: budgetData.status,
                width: budgetData.width,
                depth: budgetData.depth,
                height: budgetData.height,
                rooms: budgetData.rooms, // Salvar cômodos
                margin_loss: 0.10,
            })
            .select()
            .single();

        if (budgetError || !budget) {
            console.error('Erro ao criar orçamento:', budgetError);
            return null;
        }

        // Inserir itens
        if (budgetData.items && budgetData.items.length > 0) {
            const itemsToInsert = budgetData.items.map(item => ({
                budget_id: budget.id,
                room_id: item.room_id, // Salvar o vinculo com o cômodo
                service_type: item.service_type,
                quantity: item.quantity,
                unit_price: item.unit_price,
                subtotal: item.subtotal,
            }));

            const { error: itemsError } = await supabase
                .from('budget_items')
                .insert(itemsToInsert);

            if (itemsError) {
                console.error('Erro ao criar itens do orçamento:', itemsError);
            }
        }

        // Buscar orçamento completo com itens
        const { data: items } = await supabase
            .from('budget_items')
            .select('*')
            .eq('budget_id', budget.id);

        return dbToApp(budget as BudgetDB, (items || []) as BudgetItemDB[]);
    },

    // Atualizar status do orçamento
    async updateBudgetStatus(
        budgetId: string,
        status: 'pendente' | 'aprovado' | 'cancelado'
    ): Promise<boolean> {
        const { error } = await supabase
            .from('budgets')
            .update({ status })
            .eq('id', budgetId);

        if (error) {
            console.error('Erro ao atualizar status:', error);
            return false;
        }

        return true;
    },

    // Deletar orçamento
    async deleteBudget(budgetId: string): Promise<boolean> {
        // Os itens serão deletados automaticamente devido ao ON DELETE CASCADE
        const { error } = await supabase
            .from('budgets')
            .delete()
            .eq('id', budgetId);

        if (error) {
            console.error('Erro ao deletar orçamento:', error);
            return false;
        }

        return true;
    },

    // Buscar orçamento por ID
    async getBudgetById(budgetId: string): Promise<Budget | null> {
        const { data: budget, error: budgetError } = await supabase
            .from('budgets')
            .select('*')
            .eq('id', budgetId)
            .single();

        if (budgetError || !budget) {
            console.error('Erro ao buscar orçamento:', budgetError);
            return null;
        }

        const { data: items } = await supabase
            .from('budget_items')
            .select('*')
            .eq('budget_id', budgetId);

        return dbToApp(budget as BudgetDB, (items || []) as BudgetItemDB[]);
    },
};

// Função utilitária para calcular totais
export const calculateTotal = (
    items: { type: string; quantity: number; price: number }[]
) => {
    const processedItems = items.map(item => ({
        service_type: item.type,
        quantity: item.quantity,
        unit_price: item.price,
        subtotal: item.quantity * item.price,
    }));

    const total = processedItems.reduce((acc, curr) => acc + curr.subtotal, 0);

    return {
        items: processedItems,
        total_value: total,
    };
};
